import { Component, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TrainerResponse, TrainerImageResponse } from '../../@shared/models/response.module';
import { TRN_13, TRN_16 } from '../../@shared/models/input.module';
import { TrainerService } from '../../@shared/services/trainer.service';
import { ChatService } from '../../@shared/services/chat.service';
import { JwtService } from '../../@core/services/jwt.service';
import { StorageService } from '../../@core/services/storage.service';
import { ToastService } from '../../@core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-trainer-details',
  standalone: false,
  templateUrl: './trainer-details.html',
  styleUrl: './trainer-details.css'
})
export class TrainerDetails implements OnInit {
  trainer: TrainerResponse | null = null;
  loading: boolean = false;
  error: string | null = null;

  // Events
  upcomingEvents: any[] = [];
  pastEvents: any[] = [];
  loadingEvents: boolean = false;

  // Images / Gallery
  images: TrainerImageResponse[] = [];
  loadingImages: boolean = false;
  showLightbox: boolean = false;
  currentImageIndex: number = 0;

  // Message modal
  showMessageModal: boolean = false;
  messageText: string = '';
  sendingMessage: boolean = false;

  // Recommendation modal
  showRecommendModal: boolean = false;
  submittingRecommendation: boolean = false;
  recommendForm: TRN_16 = {
    trainer_name: '',
    description: '',
    specializations: '',
    contact_info: ''
  };

  // Current user trainer status
  currentUserIsTrainer: boolean = false;

  // Application modal
  showApplicationModal: boolean = false;
  submittingApplication: boolean = false;
  applicationForm: TRN_13 = {
    trainer_description: '',
    trainer_experience: '',
    trainer_certificates: '',
    trainer_achievements: '',
    trainer_specializations: '',
    contact_email: '',
    contact_phone: ''
  };

  // Predefined specializations - matches event disciplines
  availableSpecializations: string[] = [
    'dressage', 'jumping', 'eventing', 'driving', 'vaulting',
    'distance', 'icelandic', 'western', 'para_dressage',
    'mounted_games', 'working_eq', 'polo', 'other'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private trainerService: TrainerService,
    private chatService: ChatService,
    private jwtService: JwtService,
    private storageService: StorageService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    const userId = this.route.snapshot.paramMap.get('id');
    if (userId) {
      this.loadTrainer(userId);
    }

    // Check if current user is already a trainer
    if (this.isLoggedIn()) {
      this.trainerService.getStatus().subscribe({
        next: (status) => {
          this.currentUserIsTrainer = status?.is_trainer === true && status?.trainer_status === 'active';
        },
        error: () => {}
      });
    }
  }

  loadTrainer(userId: string) {
    this.loading = true;
    this.error = null;

    this.trainerService.getTrainer(userId).subscribe({
      next: (trainer) => {
        this.trainer = trainer;
        this.loading = false;
        this.loadEvents(userId);
        this.loadImages(userId);
      },
      error: (err) => {
        this.error = this.translateService.instant('trainerDetails.errorLoading');
        this.loading = false;
      }
    });
  }

  loadEvents(userId: string) {
    this.loadingEvents = true;

    this.trainerService.getTrainerEvents(userId).subscribe({
      next: (data) => {
        this.upcomingEvents = data.upcoming_events || [];
        this.pastEvents = data.past_events || [];
        this.loadingEvents = false;
      },
      error: () => {
        this.loadingEvents = false;
      }
    });
  }

  formatEventDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const locale = this.translateService.currentLang === 'no' ? 'nb-NO' : 'en-US';
    return date.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  viewEvent(event: any) {
    this.router.navigate(['/event-details', event.event_id]);
  }

  getEventLocation(event: any): string {
    const parts = [event.city, event.country].filter(p => p);
    return parts.join(', ');
  }

  loadImages(userId: string) {
    this.loadingImages = true;

    this.trainerService.getTrainerImages(userId).subscribe({
      next: (images) => {
        this.images = images || [];
        this.loadingImages = false;
      },
      error: () => {
        this.loadingImages = false;
      }
    });
  }

  openLightbox(index: number) {
    this.currentImageIndex = index;
    this.showLightbox = true;
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    this.showLightbox = false;
    document.body.style.overflow = '';
  }

  prevImage() {
    if (this.currentImageIndex > 0) {
      this.currentImageIndex--;
    } else {
      this.currentImageIndex = this.images.length - 1;
    }
  }

  nextImage() {
    if (this.currentImageIndex < this.images.length - 1) {
      this.currentImageIndex++;
    } else {
      this.currentImageIndex = 0;
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.showLightbox) return;

    switch (event.key) {
      case 'Escape':
        this.closeLightbox();
        break;
      case 'ArrowLeft':
        this.prevImage();
        break;
      case 'ArrowRight':
        this.nextImage();
        break;
    }
  }

  getInitials(): string {
    if (!this.trainer) return '';
    const first = this.trainer.first_name?.charAt(0) || '';
    const last = this.trainer.last_name?.charAt(0) || '';
    return (first + last).toUpperCase();
  }

  getSpecializationsList(): string[] {
    if (!this.trainer?.trainer_specializations) return [];
    return this.trainer.trainer_specializations.split(',').map(s => s.trim()).filter(s => s);
  }

  translateSpecialization(spec: string): string {
    const oldToKey: { [key: string]: string } = {
      'Dressage': 'dressage', 'Jumping': 'jumping', 'Western': 'western',
      'Eventing': 'eventing', 'Reining': 'western', 'Trail Riding': 'distance',
      'Barrel Racing': 'western', 'Pole Bending': 'western',
      'Horsemanship': 'other', 'Ground Work': 'other', 'Liberty Training': 'other'
    };
    const key = oldToKey[spec] || spec;
    const i18nKey = `trainers.spec.${key}`;
    const translated = this.translateService.instant(i18nKey);
    return translated !== i18nKey ? translated : spec;
  }

  getCertificatesList(): string[] {
    if (!this.trainer?.trainer_certificates) return [];
    return this.trainer.trainer_certificates.split(',').map(s => s.trim()).filter(s => s);
  }

  getAchievementsList(): string[] {
    if (!this.trainer?.trainer_achievements) return [];
    return this.trainer.trainer_achievements.split(',').map(s => s.trim()).filter(s => s);
  }

  getLocation(): string {
    if (!this.trainer) return '';
    const parts = [this.trainer.city, this.trainer.state, this.trainer.country].filter(p => p);
    return parts.join(', ');
  }

  hasSocialLinks(): boolean {
    if (!this.trainer) return false;
    return !!(
      this.trainer.trainer_website_url ||
      this.trainer.trainer_facebook_url ||
      this.trainer.trainer_instagram_url ||
      this.trainer.trainer_youtube_url ||
      this.trainer.trainer_tiktok_url
    );
  }

  goBack() {
    this.router.navigate(['/trainers']);
  }

  // Message methods
  openMessageModal() {
    if (!this.jwtService.getAccessToken()) {
      this.router.navigate(['/auth/login']);
      return;
    }
    this.showMessageModal = true;
  }

  closeMessageModal() {
    this.showMessageModal = false;
    this.messageText = '';
  }

  sendMessage() {
    if (!this.messageText.trim() || !this.trainer) return;

    this.sendingMessage = true;

    this.chatService.startConversation({
      recipient_user_id: this.trainer.user_id,
      subject: `Message from Trainers page`,
      initial_message: this.messageText
    }).subscribe({
      next: () => {
        this.sendingMessage = false;
        this.closeMessageModal();
        // Optionally navigate to chat
        this.router.navigate(['/chat']);
      },
      error: () => {
        this.sendingMessage = false;
      }
    });
  }

  // ==================== AUTH CHECK ====================

  isLoggedIn(): boolean {
    return !!this.jwtService.getAccessToken();
  }

  // ==================== RECOMMENDATION METHODS ====================

  openRecommendModal() {
    this.showRecommendModal = true;
  }

  closeRecommendModal() {
    this.showRecommendModal = false;
  }

  submitRecommendation() {
    if (!this.recommendForm.trainer_name?.trim()) {
      this.toastService.error(this.translateService.instant('trainer.nameRequired'));
      return;
    }
    if (!this.recommendForm.description?.trim()) {
      this.toastService.error(this.translateService.instant('trainer.reasonRequired'));
      return;
    }

    this.submittingRecommendation = true;
    this.trainerService.submitRecommendation(this.recommendForm).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('trainer.recommendationSubmitted'));
        this.showRecommendModal = false;
        this.submittingRecommendation = false;
        this.recommendForm = {
          trainer_name: '',
          description: '',
          specializations: '',
          contact_info: ''
        };
      },
      error: () => {
        this.submittingRecommendation = false;
      }
    });
  }

  toggleRecommendSpecialization(spec: string) {
    const currentSpecs = (this.recommendForm.specializations || '')
      .split(',').map(s => s.trim()).filter(s => s);
    const index = currentSpecs.indexOf(spec);
    if (index >= 0) {
      currentSpecs.splice(index, 1);
    } else {
      currentSpecs.push(spec);
    }
    this.recommendForm.specializations = currentSpecs.join(', ');
  }

  isRecommendSpecializationSelected(spec: string): boolean {
    const currentSpecs = (this.recommendForm.specializations || '')
      .split(',').map(s => s.trim()).filter(s => s);
    return currentSpecs.includes(spec);
  }

  // ==================== APPLICATION METHODS ====================

  openApplicationModal() {
    const user = this.storageService.getUser();
    if (user) {
      this.applicationForm.contact_email = user.email || '';
      this.applicationForm.contact_phone = user.phone || '';
    }
    this.showApplicationModal = true;
  }

  closeApplicationModal() {
    this.showApplicationModal = false;
  }

  submitApplication() {
    if (!this.applicationForm.trainer_description?.trim()) {
      this.toastService.error(this.translateService.instant('trainer.descriptionRequired'));
      return;
    }
    if (!this.applicationForm.trainer_experience?.trim()) {
      this.toastService.error(this.translateService.instant('trainer.experienceRequired'));
      return;
    }

    this.submittingApplication = true;
    this.trainerService.submitApplication(this.applicationForm).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('trainer.applicationSubmitted'));
        this.showApplicationModal = false;
        this.submittingApplication = false;
        this.applicationForm = {
          trainer_description: '',
          trainer_experience: '',
          trainer_certificates: '',
          trainer_achievements: '',
          trainer_specializations: '',
          contact_email: '',
          contact_phone: ''
        };
      },
      error: () => {
        this.submittingApplication = false;
      }
    });
  }

  toggleApplicationSpecialization(spec: string) {
    const currentSpecs = (this.applicationForm.trainer_specializations || '')
      .split(',').map(s => s.trim()).filter(s => s);
    const index = currentSpecs.indexOf(spec);
    if (index >= 0) {
      currentSpecs.splice(index, 1);
    } else {
      currentSpecs.push(spec);
    }
    this.applicationForm.trainer_specializations = currentSpecs.join(', ');
  }

  isApplicationSpecializationSelected(spec: string): boolean {
    const currentSpecs = (this.applicationForm.trainer_specializations || '')
      .split(',').map(s => s.trim()).filter(s => s);
    return currentSpecs.includes(spec);
  }
}
